import React, { useCallback, useEffect, useMemo } from 'react';
import { Flex, VStack } from '@televet/kibble-ui/build/chakra';
import { DatePicker, DateRange } from '@televet/kibble-ui/build/components/DatePicker';
import { TimeRange } from '@televet/kibble-ui/build/components/TimeRange';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { updateAppointmentDraft, updateAppointmentModalErrors } from 'pages/Appointments/state/appointmentsModalSlice';
import addMinutes from 'date-fns/addMinutes';
import differenceInMinutes from 'date-fns/fp/differenceInMinutes';
import useClinicUser from 'shared/hooks/useClinicUser';
import { DEFAULT_APPOINTMENT_DURATION_IN_MINUTES } from 'shared/providers/ApolloProvider/reactiveVars/appointmentFilters';
import startOfDay from 'date-fns/startOfDay';
import { ModuleName, useModularity } from 'shared/hooks/useModularity';
import { TimePicker } from '@televet/kibble-ui';

type TimeInput = 'startTime' | 'endTime';

export const AppointmentDateTime = (): JSX.Element => {
  const { isAppointmentRescheduleSupported } = useIntegrationsProvider();
  const { currentClinic } = useClinicUser();
  const { hasModule } = useModularity();
  const isStandardClinic = hasModule(ModuleName.AppointmentsStandard);
  const {
    appointmentDraft: { startAt, durationInMinutes, pimsId },
    errors,
  } = useAppSelector((state) => state.appointmentsModal);
  const dispatch = useAppDispatch();

  const defaultDurationInMinutes = useMemo(() => {
    return (
      currentClinic?.clinicSetting?.appointmentTimeSlotIncrementInMinutes || DEFAULT_APPOINTMENT_DURATION_IN_MINUTES
    );
  }, [currentClinic]);

  const appointmentDate = useMemo(() => {
    if (startAt) {
      return new Date(startAt);
    } else {
      return startOfDay(new Date());
    }
  }, [startAt]);

  const calculatedTimeValues = useMemo(() => {
    if (!appointmentDate) return;

    return {
      startTime: appointmentDate,
      endTime: addMinutes(appointmentDate, durationInMinutes || defaultDurationInMinutes),
    };
  }, [appointmentDate, defaultDurationInMinutes, durationInMinutes]);

  const cannotEditDateTime = useMemo(
    () => !isAppointmentRescheduleSupported && !!pimsId,
    [pimsId, isAppointmentRescheduleSupported],
  );

  const handleTimeChange = useCallback(
    (time: Date, inputType: TimeInput) => {
      if (!appointmentDate || !calculatedTimeValues) return;
      const { startTime } = calculatedTimeValues;
      const newDate = new Date(appointmentDate.toISOString());

      // The time picker returns the current date with the selected time value
      // We need to manually set the existing date value with the new time
      newDate.setHours(time.getHours());
      newDate.setMinutes(time.getMinutes());

      if (inputType === 'startTime') {
        dispatch(updateAppointmentDraft({ startAt: newDate?.toISOString() }));
      } else {
        dispatch(
          updateAppointmentDraft({
            durationInMinutes: differenceInMinutes(startTime, newDate),
          }),
        );
        dispatch(updateAppointmentModalErrors(errors.filter((error) => error !== 'missingEndTime')));
      }
    },
    [appointmentDate, calculatedTimeValues, dispatch, errors],
  );

  const handleDateChange = useCallback(
    ({ startDate }: DateRange) => {
      // The date picker returns the current date with the time value defaulted to midnight
      // We need to manually set the existing time values on the new appointment date if provided
      startDate?.setHours(calculatedTimeValues?.startTime?.getHours() || 0);
      startDate?.setMinutes(calculatedTimeValues?.startTime?.getMinutes() || 0);

      dispatch(updateAppointmentDraft({ startAt: startDate?.toISOString() }));
      dispatch(updateAppointmentModalErrors(errors.filter((error) => error !== 'missingStartTime')));
    },
    [calculatedTimeValues?.startTime, dispatch, errors],
  );

  // If the appointment date is not set, default to today
  // this is to ensure the date picker has a value to start with
  useEffect(() => {
    if (!startAt) {
      handleDateChange({ startDate: new Date() });
    }
  }, [handleDateChange, startAt]);

  return (
    <>
      <Flex
        className="AppointmentModal__DateTimeContainer"
        align="flex-start"
        flexDirection={isStandardClinic ? 'row' : 'column'}
        gap={2}
        w="100%"
      >
        <VStack className="AppointmentModal__Date" spacing={0} align="flex-start" w="100%">
          <Text fontWeight="bold" mb={2}>
            Appointment Date
          </Text>
          <DatePicker
            startDate={appointmentDate || null}
            onDateChange={handleDateChange}
            inputProps={{
              isDisabled: cannotEditDateTime,
              isInvalid: errors.includes('missingStartTime'),
              errorText: 'Please select a date',
            }}
          />
        </VStack>
        <VStack className="AppointmentModal__Time" spacing={0} align="flex-start" w="100%">
          <Text fontWeight="bold" mb={2}>
            Appointment Time
          </Text>
          {isStandardClinic ? (
            <TimeRange
              startTimePickerProps={{
                initialTime: calculatedTimeValues?.startTime,
                placement: 'bottom',
                onTimeChange: (time: Date): void => handleTimeChange(time, 'startTime'),
                inputProps: {
                  isDisabled: cannotEditDateTime,
                },
              }}
              endTimePickerProps={{
                initialTime: calculatedTimeValues?.endTime,
                placement: 'bottom',
                onTimeChange: (time: Date): void => handleTimeChange(time, 'endTime'),
                inputProps: {
                  isDisabled: cannotEditDateTime,
                  isInvalid: errors.includes('missingEndTime'),
                  errorText: 'An end time must be provided and cannot be before the provided start time',
                },
              }}
            />
          ) : (
            <TimePicker
              initialTime={calculatedTimeValues?.startTime}
              placement="bottom"
              onTimeChange={(time: Date): void => handleTimeChange(time, 'startTime')}
              inputProps={{
                isDisabled: cannotEditDateTime,
              }}
            />
          )}
        </VStack>
      </Flex>
      {cannotEditDateTime && (
        <Alert status="warning" description="All Neo appointment reschedules should be done in Neo." hideCloseButton />
      )}
    </>
  );
};
